import { useEffect, useState, useSyncExternalStore } from 'react';
import { ThemeOptions } from '@mui/material';
import RequestTypeList, { RequestTypeListProps } from './RequestTypeList';
import { useTask } from 'vig-ui-library';
import { RequestApiRouteConfig, useRequestApi } from '../RequestForm/Api/RequestApi';
import { GetRequestTypesWithTagsInputSchema } from '../RequestForm/Schemas/GetRequestTypesWithTagsInput';
import { RequestTagData } from '../RequestForm/Schemas/RequestTagData';
import { RequestTypeWithTagsDto } from '../RequestForm/Schemas/RequestTypeWithTagsDto';
import { Axios } from 'axios';
import { CompanyType } from '../Enums/CompanyType';
import { ThemeProvider } from '../ThemeProvider';
import { RequestStepListProps } from './RequestStepList';

export type RequestTypeCatalogProvidersProps = {
    children: React.ReactNode;
    themeOverride?: ThemeOptions;
    companyType: CompanyType;
};

export const RequestTypeCatalogProviders = (props: RequestTypeCatalogProvidersProps) => {
    return <ThemeProvider
        companyType={props.companyType}
        themeOverride={props.themeOverride}
    >{props.children}</ThemeProvider>;
};

export type RequestTypeCatalogProps = {
    axiosInstance: Axios;
    apiRouteConfig?: RequestApiRouteConfig;
    requestTagId?: string;
    onRequestTypeSelected: RequestTypeListProps['onRequestTypeSelected'];
    onParentTagSelected: RequestTypeListProps['onParentTagSelected'];
    query: string | null;
    onParentTagsChange?: (parentTags: RequestTagData[]) => void;
    requestStepListSx?: RequestStepListProps["sx"];
    availableWidth?: number;
} & Omit<RequestTypeCatalogProvidersProps, 'children'>;

function subscribeViewportWidth(callback: () => void) {
    window.addEventListener('resize', callback);
    return () => window.removeEventListener('resize', callback);
}

function getViewportWidthSnapshot() {
    return typeof window !== 'undefined' ? window.innerWidth : 1024;
}

function getViewportWidthServerSnapshot() {
    return 1024;
}

function useViewportWidthForCatalog() {
    return useSyncExternalStore(subscribeViewportWidth, getViewportWidthSnapshot, getViewportWidthServerSnapshot);
}

export function RequestTypeCatalog({ axiosInstance, apiRouteConfig, requestTagId, query, companyType, themeOverride, onParentTagsChange, availableWidth, ...rest }: RequestTypeCatalogProps) {
    const requestApi = useRequestApi(axiosInstance, apiRouteConfig);
    const viewportWidth = useViewportWidthForCatalog();
    const resolvedAvailableWidth = availableWidth ?? viewportWidth;

    const [requestTypes, setRequestTypes] = useState<RequestTypeWithTagsDto[]>([]);

    const rt = requestTagId;

    const q = query;

    const getAllRequestTypesTask = useTask(async () => {
        try {
            const input = GetRequestTypesWithTagsInputSchema.parse({
                searchQuery: q,
                parentTagId: rt,
            });

            const result = await requestApi.getAllRequestsTypes(input);

            onParentTagsChange?.(result.parentTags || []);
            setRequestTypes(result.requestTypeAndTags);
        } catch (error) {
            // Keep at least console visibility for wrapper integrations.
            console.error("RequestTypeCatalog: failed to load request types", error);
        }
    });

    useEffect(() => {
        if (!getAllRequestTypesTask.isInitialCall) {
            getAllRequestTypesTask.debouncedPerform();
        } else {
            getAllRequestTypesTask.perform();
        }
    }, [rt, q]);

    return (
        <RequestTypeCatalogProviders companyType={companyType} themeOverride={themeOverride} >
            <RequestTypeList
                {...rest}
                availableWidth={resolvedAvailableWidth}
                requestTypes={requestTypes}
                getAllRequestTypesTask={getAllRequestTypesTask}
            />
        </RequestTypeCatalogProviders>
    );
}
